import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter, Route, Routes, Navigate, useLocation, useNavigationType, useNavigate } from 'react-router-dom';
import { CookiesProvider } from 'react-cookie';
import { AnimatePresence, motion } from 'framer-motion';
import { ThemeProvider } from './contexts/ThemeContext';
import StockMainPage from './components/StockMainPage';
import StockDetailPage from './components/StockDetailPage';
import CommunityPage from './components/Community/CommunityPage';
import Login from './components/OAuth/Login';
import ProfilePage from './components/Profile/ProfilePage';
import ArticlePage from './components/Article/ArticlePage';
import AppStorePage from './components/AppStorePage';

const EASE = [0.25, 0.46, 0.45, 0.94];

const pageVariants = {
  initial: ({ isBack } = {}) => ({
    x: isBack ? '-25%' : '100%',
    opacity: isBack ? 0.7 : 0,
  }),
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      x:       { type: 'tween', duration: 0.38, ease: EASE },
      opacity: {                duration: 0.3,  ease: EASE },
    },
  },
  exit: ({ isBack } = {}) => ({
    x: isBack ? '100%' : '-25%',
    opacity: isBack ? 0 : 0.7,
    transition: {
      x:       { type: 'tween', duration: 0.38, ease: EASE },
      opacity: {                duration: 0.3,  ease: EASE },
    },
  }),
};

// 애니메이션 div: overflow: hidden → GPU 컴포지터 레이어 분리 가능
const pageStyle = {
  position: 'fixed',
  inset: 0,
  overflow: 'hidden',
  backgroundColor: 'transparent',
  zIndex: 1,
  willChange: 'transform', // GPU 레이어 미리 승격
};

// 스크롤은 내부 div가 담당
const scrollStyle = {
  height: '100%',
  overflowY: 'auto',
  overflowX: 'hidden',
  WebkitOverflowScrolling: 'touch', // iOS 모멘텀 스크롤
};

const REFRESHABLE = ['/stockMain', '/community'];
const THRESHOLD = 58;   // 시각적 트리거 거리 (저항 적용 후)
const MAX_PULL  = 88;   // 시각적 최대 당김 거리
const RESIST    = 0.48; // rubber-band 저항 계수

const PTR_SIZE = 28;
const PTR_R    = (PTR_SIZE - 3) / 2;
const PTR_CIRC = 2 * Math.PI * PTR_R;

const Wrap = ({ children, isBack, refreshable }) => {
  const scrollRef  = useRef(null);
  const startYRef  = useRef(0);
  const activeRef  = useRef(false);
  const [pullY,     setPullY]     = useState(0);
  const [triggered, setTriggered] = useState(false);
  const [releasing, setReleasing] = useState(false);

  const onTouchStart = (e) => {
    if (!refreshable) return;
    const el = scrollRef.current;
    if (el && el.scrollTop > 0) return;
    startYRef.current = e.touches[0].clientY;
    activeRef.current = true;
    setReleasing(false);
  };

  const onTouchMove = (e) => {
    if (!refreshable || !activeRef.current) return;
    const el = scrollRef.current;
    if (!el || el.scrollTop > 0) { activeRef.current = false; return; }
    const delta = e.touches[0].clientY - startYRef.current;
    if (delta <= 0) return;
    const y = Math.min(delta * RESIST, MAX_PULL);
    setPullY(y);
    setTriggered(y >= THRESHOLD);
  };

  const onTouchEnd = () => {
    if (!refreshable || !activeRef.current) return;
    activeRef.current = false;
    const wasTriggered = triggered;
    setReleasing(true);
    setPullY(0);
    setTriggered(false);
    if (wasTriggered) setTimeout(() => window.location.reload(), 380);
  };

  const progress = Math.min(pullY / THRESHOLD, 1);

  return (
    <motion.div
      custom={{ isBack }}
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={pageStyle}
    >
      <div
        ref={scrollRef}
        style={scrollStyle}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        {refreshable && (
          <div style={{
            display: 'flex', justifyContent: 'center', alignItems: 'center',
            height: pullY,
            overflow: 'hidden',
            transition: releasing
              ? 'height 0.38s cubic-bezier(0.25, 0.46, 0.45, 0.94)'
              : 'none',
          }}>
            {pullY > 2 && (
              <svg
                width={PTR_SIZE} height={PTR_SIZE}
                style={{
                  flexShrink: 0,
                  opacity: Math.min(progress * 1.5, 1),
                  transform: triggered ? undefined : `rotate(${progress * 260 - 90}deg)`,
                  animation: triggered ? 'ptr-spin 0.75s linear infinite' : 'none',
                  transition: releasing ? 'opacity 0.2s' : 'none',
                }}
              >
                {/* 배경 트랙 */}
                <circle
                  cx={PTR_SIZE / 2} cy={PTR_SIZE / 2} r={PTR_R}
                  fill="none" stroke="currentColor" strokeWidth="2.5" strokeOpacity="0.18"
                />
                {/* 프로그레스 arc */}
                <circle
                  cx={PTR_SIZE / 2} cy={PTR_SIZE / 2} r={PTR_R}
                  fill="none" stroke="currentColor" strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeDasharray={PTR_CIRC}
                  strokeDashoffset={triggered ? 0 : PTR_CIRC * (1 - progress)}
                  transform={`rotate(-90 ${PTR_SIZE / 2} ${PTR_SIZE / 2})`}
                  style={{ transition: triggered ? 'none' : 'stroke-dashoffset 0.04s linear' }}
                />
              </svg>
            )}
          </div>
        )}
        {children}
      </div>
    </motion.div>
  );
};

// 네이티브 → 웹 탭 네비게이션 브릿지
// window.navigateTo(path)를 native가 호출하면 React Router로 라우팅
const NavigationBridge = () => {
  const navigate = useNavigate();
  useEffect(() => {
    window.__navigate = navigate;
    return () => { window.__navigate = null; };
  }, [navigate]);
  return null;
};

const AnimatedRoutes = () => {
  const location = useLocation();
  const navType = useNavigationType();
  const isBack = navType === 'POP' || location.state?._navDirection === 'back';
  const custom = { isBack };

  return (
    <AnimatePresence mode="sync" initial={false} custom={custom}>
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Navigate to="/stockMain" />} />
        <Route path="/stockMain" element={<Wrap isBack={isBack} refreshable><StockMainPage /></Wrap>} />
        <Route path="/stockDetail/:stockName" element={<Wrap isBack={isBack} refreshable><StockDetailPage /></Wrap>} />
        <Route path="/community" element={<Wrap isBack={isBack} refreshable><CommunityPage /></Wrap>} />
        <Route path="/profile" element={<Wrap isBack={isBack}><ProfilePage /></Wrap>} />
        <Route path="/login" element={<Wrap isBack={isBack}><Login /></Wrap>} />
        <Route path="/article" element={<Wrap isBack={isBack}><ArticlePage /></Wrap>} />
        <Route path="/appStore" element={<AppStorePage />} />
      </Routes>
    </AnimatePresence>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <ThemeProvider>
    <CookiesProvider>
      <BrowserRouter>
        <NavigationBridge />
        <AnimatedRoutes />
      </BrowserRouter>
    </CookiesProvider>
  </ThemeProvider>
);

reportWebVitals();
